import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import SocialLinks from "@/components/SocialLinks";
import { trackEvent } from "@/components/RouteTracker";
import { CheckCircle2, Phone, Mail, MapPin, Send } from "lucide-react";

const SERVICES = [
  { value: "new_construction", label: "New Construction" },
  { value: "remodel", label: "Remodel" },
  { value: "renovation", label: "Renovation" },
  { value: "floor_plan", label: "Floor Plan Inquiry" },
  { value: "other", label: "Other / Not sure yet" },
];

export default function Contact() {
  const [params] = useSearchParams();
  const initialService = params.get("service") || "new_construction";
  const planId = params.get("plan") || null;
  const [planName, setPlanName] = useState(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: initialService,
    message: planId ? `I'm interested in floor plan with ID ${planId}. Please get in touch.` : "",
  });
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!planId) return;
    api.get(`/floor-plans/${planId}`).then((r) => {
      setPlanName(r.data.name);
      setForm((f) => ({ ...f, message: `I'm interested in the "${r.data.name}" floor plan. Please get in touch.` }));
    }).catch(() => {});
  }, [planId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/leads", { ...form, floor_plan_id: planId });
      trackEvent("generate_lead", {
        service: form.service,
        method: planId ? "floor_plan_inquiry" : "contact_form",
        currency: "USD",
        value: 1,
      });
      setSuccess(true);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="label-overline mb-4">Get in touch</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-5">
            Let&rsquo;s talk about your <span style={{color:'var(--accent)'}}>build.</span>
          </h1>
          <p className="text-base leading-relaxed mb-8" style={{color:'var(--text-muted)'}}>
            Tell us a little about what you&rsquo;re planning. We respond within one business day with a clear path forward — and a real human, not a chatbot.
          </p>

          <ul className="space-y-4">
            <ContactRow icon={Phone} label="(361) 833-1309" />
            <ContactRow icon={Mail} label="info@coastalbendcontractor.com" />
            <ContactRow icon={MapPin} label="Corpus Christi, TX · Nueces County" />
          </ul>

          <div className="mt-8">
            <div className="label-overline mb-4">Follow us</div>
            <SocialLinks variant="labeled" />
          </div>

          {planName && (
            <div className="mt-8 card p-4 text-sm">
              <span style={{color:'var(--text-muted)'}}>Inquiring about: </span>
              <span style={{color:'var(--accent)'}} className="font-semibold">{planName}</span>
            </div>
          )}
        </div>

        <div className="lg:col-span-7">
          <div className="card p-7 lg:p-9">
            {success ? (
              <div className="text-center py-8" data-testid="lead-success">
                <CheckCircle2 size={56} className="mx-auto mb-4" style={{color:'var(--accent)'}} />
                <h2 className="font-display text-2xl font-bold mb-2">Got it. We&rsquo;ll be in touch.</h2>
                <p style={{color:'var(--text-muted)'}}>One of our team will reach out within one business day.</p>
                <Link to="/" className="btn-ghost mt-6">Back to home</Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <h2 className="font-display text-2xl font-bold mb-2">Request a Quote</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Full name" required>
                    <input className="input" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="lead-name" />
                  </Field>
                  <Field label="Email" required>
                    <input type="email" className="input" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="lead-email" />
                  </Field>
                  <Field label="Phone">
                    <input className="input" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="lead-phone" />
                  </Field>
                  <Field label="Service">
                    <select className="input" value={form.service} onChange={(e) => setForm({...form, service: e.target.value})} data-testid="lead-service">
                      {SERVICES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Tell us about your project">
                  <textarea className="input" rows={5} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Lot location, square footage goals, timeline, budget range — anything that helps us help you." data-testid="lead-message" />
                </Field>
                {error && <div className="text-sm" style={{color:'#fca5a5'}} data-testid="lead-error">{error}</div>}
                <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="lead-submit">
                  {busy ? "Sending…" : (<>Send Request <Send size={15}/></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{color:'var(--text-muted)'}}>
        {label}{required && <span style={{color:'var(--accent)'}}> *</span>}
      </div>
      {children}
    </label>
  );
}

function ContactRow({ icon: Icon, label }) {
  return (
    <li className="flex items-center gap-3">
      <span className="w-9 h-9 rounded-md flex items-center justify-center" style={{background: 'rgba(212,163,115,0.1)'}}>
        <Icon size={15} style={{color:'var(--accent)'}}/>
      </span>
      <span>{label}</span>
    </li>
  );
}
